import React, { useState, ReactNode } from 'react';
import { BaseDrawer } from '@/components';
import { DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import useNotifications from './useNotifications';
import { Spinner } from '@/components';
import { BaseButton } from '@/components/uiKit/Button';
import { NotificationsList } from './NotificationsList';
import { Icon } from '@/components/shared/display/Icon';
import { CloseIcon, TrashIcon } from '@/assets/Icons';

interface NotificationsDrawerContentProps {
  isVisible: boolean;
}

const NotificationsDrawerContent = ({ isVisible }: NotificationsDrawerContentProps) => {
  const [isDialogOpened, setDialogOpened] = useState(false);

  const { isLoading, isClearingNotifications, clearAllNotifications, enabledNotifications } =
    useNotifications(isVisible);

  const handleDialogCancelClick = () => {
    setDialogOpened(false);
  };

  const handleDialogDeleteClick = async () => {
    setDialogOpened(false);
    await clearAllNotifications();
  };

  const handleClearAllClick = () => {
    setDialogOpened(true);
  };

  return (
    <DrawerContent
      className="bg-background after:hidden left-sidebar w-[var(--notifications-drawer-width)] rounded-none h-full flex flex-col"
      style={{ '--initial-transform': 'calc(100% + var(--sidebar-width))' } as React.CSSProperties}
      data-vaul-no-drag
    >
      <DrawerHeader className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
        <DrawerTitle>Alerts</DrawerTitle>
        <DrawerClose>
          <Icon icon={CloseIcon} />
        </DrawerClose>
      </DrawerHeader>
      {(isLoading || isClearingNotifications) && (
        <div className="h-full w-full flex justify-center items-center">
          <Spinner size="large" />
        </div>
      )}
      {!isLoading && !isClearingNotifications && !!enabledNotifications.length && (
        <>
          <div className="h-full w-full flex-1 overflow-y-auto min-h-0">
            <NotificationsList notifications={enabledNotifications} />
          </div>
          <div className="w-full flex flex-col justify-center h-24 shadow-[0_-2px_5px_rgba(0,0,0,0.1)]">
            {isDialogOpened ? (
              <div className="p-0 text-center">
                <div className="text-sm pb-2 dm-text-primary">Are you sure you want to delete all notifications?</div>
                <div className="flex justify-center gap-2">
                  <BaseButton
                    size="xs"
                    variant="filled"
                    onClick={handleDialogCancelClick}
                    className="px-2 py-1 h-auto text-xs font-semibold text-inherit bg-gray-300 bg-opacity-30 hover:bg-opacity-70 hover:bg-gray-300 active:bg-opacity-100 active:bg-gray-300"
                  >
                    Cancel
                  </BaseButton>
                  <BaseButton
                    size="xs"
                    variant="filled"
                    onClick={handleDialogDeleteClick}
                    className="px-2 py-1 h-auto text-stone-50 text-xs font-semibold bg-red-600 hover:bg-opacity-70 hover:bg-red-600 active:bg-opacity-100 active:bg-red-600"
                  >
                    Delete
                  </BaseButton>
                </div>
              </div>
            ) : (
              <BaseButton
                size="sm"
                variant="outline"
                leftIcon={<Icon icon={TrashIcon} className="!w-5 !h-5" />}
                onClick={handleClearAllClick}
                className="min-h-5 min-w-5 ring-red-700 hover:bg-red-600 hover:bg-opacity-5 hover:ring-red-700 active:ring-1 text-red-700 gap-0 h-7"
              >
                CLEAR ALL
              </BaseButton>
            )}
          </div>
        </>
      )}
    </DrawerContent>
  );
};

interface NotificationsDrawerProps {
  children: ReactNode;
  onActiveChange: (isOpen: boolean) => void;
}

export const NotificationsDrawer = ({ children, onActiveChange }: NotificationsDrawerProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    onActiveChange(isOpen);
    setIsVisible(isOpen);
  };

  return (
    <BaseDrawer onOpenChange={handleOpenChange} customContent={<NotificationsDrawerContent isVisible={isVisible} />}>
      {children}
    </BaseDrawer>
  );
};
